/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {ClientPreview} from '../components/ClientPreview'
import {useAppDispatch, useAppSelector} from '../../store/Store'
import {
  selectClient,
  selectClientFiltersSelection,
  selectClients,
  selectClientsPagination,
  selectClientLoading,
  setClientFiltersSelection,
} from '../Client.slice'
import {
  useClientGetByIdMutation,
  useClientLoadMoreMutation,
  useClientSearchMutation,
} from '../Client.api'
import {ClientListItem} from '../components/ClientListItem'
import {SearchList} from '../../../common/component/SearchList'
import {IClientFilter} from '../Client.model'
import { IFilterDto } from '../../../common/model/Core.model'
import {TagInputSearchControls} from '../../../common/component/TagInputSearchControls'
import { useIntl } from 'react-intl'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { getEntry, removeEntry, setEntry } from '../../../service/LocalStorage.service'

const CLIENT_FILTERS_LS_KEY = 'client_filtersSelection'

export const ClientDashboardPage: FC = () => {
  const intl = useIntl()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.CLIENTS'}),
      path: '/clients',
      isSeparator: false,
      isActive: false,
    },
  ]

  const didFetchInitialData = useRef(false)
  const didFetchNextPage = useRef(false)
  const didFetchSearch = useRef(false)

  const filterKeys = {defaultKey: IClientFilter.NAME, defaultKeyList: [
    IClientFilter.ID,
    IClientFilter.NAME,
    IClientFilter.SECTOR,
    IClientFilter.ADDRESS,
  ]}
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [isSearchControlEnabled, setIsSearchControlEnabled] = useState(false)
  const [filterSuggestions, setFilterSuggestions] = useState<string[]>([])
  const [rowSelected, setRowSelected] = useState<string | undefined>(undefined)

  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectClientFiltersSelection)

  const sanitizePersistedFilters = (candidateFilters: unknown): IFilterDto[] => {
    if (!Array.isArray(candidateFilters)) {
      return []
    }
    return candidateFilters.filter((item): item is IFilterDto => {
      if (!item || typeof item !== 'object') {
        return false
      }
      const filter = item as IFilterDto
      return (
        typeof filter.id === 'string' &&
        filterKeys.defaultKeyList.includes(filter.id as IClientFilter) &&
        typeof filter.value === 'string' &&
        typeof filter.editable === 'boolean'
      )
    })
  }

  const persistFiltersSelection = (nextFilters: IFilterDto[]) => {
    dispatch(setClientFiltersSelection(nextFilters))
    if (nextFilters.length > 0) {
      setEntry(CLIENT_FILTERS_LS_KEY, JSON.stringify(nextFilters))
    } else {
      removeEntry(CLIENT_FILTERS_LS_KEY)
    }
  }

  const setFilters = (nextFilters: IFilterDto[]) => {
    persistFiltersSelection(nextFilters)
  }

  const [clientSearch] = useClientSearchMutation()
  const [clientLoadMore] = useClientLoadMoreMutation()
  const [clientGetById] = useClientGetByIdMutation()

  const clients = useAppSelector(selectClients)
  const clientsPagination = useAppSelector(selectClientsPagination)
  const client = useAppSelector(selectClient)
  const clientLoading = useAppSelector(selectClientLoading)

  useEffect(() => {
    let initialFilters: IFilterDto[] | undefined
    const savedFilters = getEntry(CLIENT_FILTERS_LS_KEY)
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters)
        const sanitizedFilters = sanitizePersistedFilters(parsedFilters)
        persistFiltersSelection(sanitizedFilters)
        initialFilters = sanitizedFilters
      } catch {
        persistFiltersSelection([])
      }
    }

    if (initialFilters && initialFilters.length > 0) {
      const filterIds = initialFilters.map((filter) => filter.id)
      setFilterSuggestions(filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key)))
    } else {
      setFilterSuggestions(filterKeys.defaultKeyList)
    }

    fetchInitialData(initialFilters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchInitialData = async (filtersOverride?: IFilterDto[]) => {
    const requestFilters = filtersOverride ?? filters
    try {
      setIsSearchLoading(true)
      if (!didFetchInitialData.current) {
        const request: any = {}
        if (requestFilters.length > 0) {
          request.filters = requestFilters
        }
        const response: any = await clientSearch(request)
        if (response.data) {
          setIsSearchControlEnabled(true)
          if (response.data.filters) {
            const filterIds = response.data.filters.map((filter) => filter.id)
            persistFiltersSelection(response.data.filters)
            setFilterSuggestions(filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key)))
          } else {
            const filterIds = requestFilters.map((filter) => filter.id)
            setFilterSuggestions(filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key)))
          }
        }
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const nextPageOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchNextPage.current) {
        await clientLoadMore({
          pagination: {
            limit: clientsPagination?.limit,
            sort: clientsPagination?.sort,
            page: clientsPagination?.page,
          },
          filters: filters,
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const searchOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchSearch.current) {
        await clientSearch({
          pagination: {
            sort: clientsPagination?.sort,
          },
          filters: filters,
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const resetSearchOnClickHandler = async () => {
    persistFiltersSelection([])
    setFilterSuggestions(filterKeys.defaultKeyList)
    await fetchInitialData([])
  }

  const rowOnClickHandler = async (id: string) => {
    setRowSelected(id)
    if (!client || client.id !== id) {
      await clientGetById({id, fields: 'LIMITED'})
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='flex mt-10'>
        <div className='row g-5'>
          <SearchList
            loading={isSearchLoading}
            rowSelected={rowSelected}
            nextPage={clientsPagination?.page}
            nextPageOnClickHandler={nextPageOnClickHandler}
            searchControlsComponent={
              <TagInputSearchControls
                loading={isSearchLoading}
                enabled={isSearchControlEnabled}
                filterKeys={filterKeys}
                filterSuggestions={filterSuggestions}
                filters={filters}
                setFilterSuggestions={setFilterSuggestions}
                setFilters={setFilters}
                createOnClickPath='/clients/new'
                createOnClickOpenNew={false}
                searchOnClickHandler={searchOnClickHandler}
                resetSearchResetOnClickHandler={resetSearchOnClickHandler}
              />
            }
            listComponent={clients.map((client, index) => (
              <div key={index}>
                <ClientListItem
                  data={client}
                  rowSelected={rowSelected}
                  onRowClick={rowOnClickHandler}
                />
                {index !== 0 && <div className='separator'></div>}
              </div>
            ))}
          />

          {rowSelected && (
            <ClientPreview
              client={client}
              loading={clientLoading}
              setRowSelected={setRowSelected}
            />
          )}
        </div>
      </div>
    </>
  )
}
